import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import {
  hasPushPermissions,
  requestPushPermissions,
} from 'dashboard/helper/pushHelper';

const DISMISS_KEY = 'masifi_push_nudge_dismissed';

// `beforeinstallprompt` can fire before any component mounts, so we capture it
// at module scope (once) and stash the deferred event for later use. Chrome on
// Android / desktop fires it; iOS never does.
const deferredPrompt = ref(null);

let installPromptListenerAttached = false;
const attachInstallPromptListener = () => {
  if (installPromptListenerAttached || typeof window === 'undefined') {
    return;
  }
  installPromptListenerAttached = true;
  window.addEventListener('beforeinstallprompt', event => {
    // Stop Chrome's default mini-infobar so we can trigger the prompt on demand.
    event.preventDefault();
    deferredPrompt.value = event;
  });
  // Once installed, the deferred prompt is no longer usable.
  window.addEventListener('appinstalled', () => {
    deferredPrompt.value = null;
  });
};
// Attach as soon as this module loads so we never miss the event.
attachInstallPromptListener();

const detectPlatform = () => {
  if (typeof navigator === 'undefined') {
    return 'desktop';
  }
  const userAgent = navigator.userAgent || '';
  const isModernIpad =
    navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  if (/iphone|ipad|ipod/i.test(userAgent) || isModernIpad) {
    return 'ios';
  }
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  return 'desktop';
};

const getPermission = () => {
  if (typeof window !== 'undefined' && 'Notification' in window) {
    return Notification.permission; // 'default' | 'granted' | 'denied'
  }
  return 'unsupported';
};

const isStandalone = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

const isDismissedThisSession = () => {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

export function usePushNudge() {
  const platform = ref(detectPlatform());
  const permission = ref(getPermission());
  const standalone = ref(isStandalone());
  const dismissed = ref(isDismissedThisSession());

  const reevaluate = () => {
    permission.value = getPermission();
    standalone.value = isStandalone();
    dismissed.value = isDismissedThisSession();
  };

  // Re-check permission/standalone when the tab regains focus: the user may
  // have flipped the browser permission or installed the PWA elsewhere.
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      reevaluate();
    }
  };

  onMounted(() => {
    document.addEventListener('visibilitychange', onVisibilityChange);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  });

  const isInstallable = computed(() => deferredPrompt.value !== null);

  const mode = computed(() => {
    // Already granted, dismissed this session, or unsupported on desktop:
    // nothing to nudge.
    if (permission.value === 'granted' || dismissed.value) {
      return 'hidden';
    }
    if (permission.value === 'unsupported') {
      // On iOS, an un-installed PWA reports notifications as unsupported but
      // becomes supported once installed → still worth guiding to install.
      if (platform.value === 'ios' && !standalone.value) {
        return 'ios-install';
      }
      return 'hidden';
    }

    // iOS only supports web push when installed as a PWA → guide to install first.
    if (platform.value === 'ios' && !standalone.value) {
      return 'ios-install';
    }

    if (permission.value === 'denied') {
      // Browser won't let us re-prompt; we can only guide to settings.
      return 'denied';
    }

    if (permission.value === 'default') {
      // A native dialog is available. On Android/desktop with an install prompt
      // available we surface the install option too.
      if (isInstallable.value && platform.value !== 'ios') {
        return 'android-install';
      }
      return 'request';
    }

    return 'hidden';
  });

  const shouldShow = computed(() => mode.value !== 'hidden');

  const requestPermission = () => {
    requestPushPermissions({
      onSuccess: () => {
        permission.value = 'granted';
      },
    });
    // Reflect the user's choice even if they grant without our callback firing
    // (e.g. permission already granted but subscription was missing).
    if (hasPushPermissions()) {
      permission.value = 'granted';
    }
  };

  const promptInstall = async () => {
    const event = deferredPrompt.value;
    if (!event) {
      return;
    }
    deferredPrompt.value = null;
    try {
      await event.prompt();
    } catch (error) {
      // Swallow: the prompt can only be used once and may reject if dismissed.
    }
  };

  const dismiss = () => {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, 'true');
    } catch (error) {
      // sessionStorage unavailable (private mode); fall back to in-memory hide.
    }
    dismissed.value = true;
  };

  return {
    platform,
    permission,
    standalone,
    isInstallable,
    mode,
    shouldShow,
    requestPermission,
    promptInstall,
    dismiss,
  };
}
